import { effect, inject, Injectable, signal } from '@angular/core';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { themeSwitcher } from '@siemens/ix';

export type ThemeType = 'light' | 'dark' | 'auto';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  translateService = inject(TranslateService);

  // Theme settings
  readonly #currentTheme = signal<ThemeType>(
    (localStorage.getItem('theme') as ThemeType) ?? 'auto'
  );
  readonly currentTheme = this.#currentTheme.asReadonly();

  // Language settings
  readonly availableLanguages = ['en', 'de']; // TODO: Make this more generic
  // (e.g. by reading available languages from the translation files)
  readonly #browserLanguage = this.translateService.getBrowserLang() ?? 'en';
  readonly #currentLanguage = signal<string>(
    localStorage.getItem('language') ??
      (this.availableLanguages.includes(this.#browserLanguage)
        ? this.#browserLanguage
        : 'en')
  );
  readonly currentLanguage = this.#currentLanguage.asReadonly();

  /**
   * Set the language of the application
   * @param lang The language to set (e.g. "en", "de"). Falls back to English if language is not available.
   */
  setLanguage(lang: string) {
    // Fallback to English if language being set is not available
    if (!this.availableLanguages.includes(lang)) {
      console.warn(`Language ${lang} is not available. Fallback to English`);
      lang = 'en';
    }

    this.#currentLanguage.set(lang);
  }

  constructor() {
    // Initialize language settings, fallback to either browser language or English
    this.translateService.setFallbackLang('en');
    this.translateService.use(this.currentLanguage());
    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.#currentLanguage.set(event.lang);
    });
    effect(() => {
      this.translateService.use(this.currentLanguage());
      localStorage.setItem('language', this.currentLanguage());
      this.setHtmlLangAttribute(this.currentLanguage());
    });

    // React on changes to the theme
    themeSwitcher.schemaChanged.on((theme: string) => {
      this.#currentTheme.set(theme as ThemeType);
    });
    effect(() => {
      this.setTheme(this.currentTheme());
      localStorage.setItem('theme', this.currentTheme());
    });
  }

  private setHtmlLangAttribute(lang: string): void {
    if (lang && typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', lang);
    }
  }

  /**
   * Helper function to properly set the theme in iX
   * @param theme The theme to set (e.g. "light", "dark" or "auto")
   */
  private setTheme(theme: ThemeType) {
    if (theme === 'auto') {
      themeSwitcher.setVariant();
    } else {
      const currentTheme = themeSwitcher.getCurrentTheme();
      // Only set the theme if it is different from the current theme --> otherwise: infinite loop
      if (currentTheme !== theme) {
        themeSwitcher.setVariant(theme);
      }
    }
  }
}
